import React, { Component, ReactNode } from 'react';

import { Button, DatePicker, Input, InputNumber } from 'antd';
import { connect } from 'react-redux';
import cx from 'classnames';
import moment from 'moment';
import type { Moment } from 'moment';

import { createNewTerm } from '../../../../slices/accounts/video/management/thunk';
import { FetchingStatus, YYYYMMDD } from '../../../../slices/accounts/video/management/interfaces';
import { RootState } from '../../../../slices/rootReducer';
import { selectCreateNewTermRequestStatus, selectDataOfLastTwoStartPeriods } from '../../../../slices/accounts/video/management/selectors';
import { TypeDispatchFunction } from '../../../../tsHelpers';

import styles from './createNewTerm.module.scss';

interface IExternalProps {
  accountId: string;
  disabled?: boolean;
  onCancel: () => void;
  onSucceeded?: () => void;
}

interface IState {
  dateStr: YYYYMMDD | undefined;
  minutes: number | undefined;
}

interface IMapStateToProps {
  createNewTermRequestStatus: FetchingStatus;
  minDateConstraint: YYYYMMDD | undefined;
}

interface IMapDispacthToProps {
  createNewTerm: TypeDispatchFunction<typeof createNewTerm>;
}

type IProps = IExternalProps & IMapStateToProps & IMapDispacthToProps;

class CreateNewTermBase extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      dateStr: undefined,
      minutes: 1000
    };

    this.disabledDateHandle = this.disabledDateHandle.bind(this);
    this.handleAccept = this.handleAccept.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.onDateCalendarDateSelected = this.onDateCalendarDateSelected.bind(this);
    this.onMinutesChanged = this.onMinutesChanged.bind(this);
  }

  componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any): void {
    if(prevProps.createNewTermRequestStatus === FetchingStatus.Loading && this.props.createNewTermRequestStatus === FetchingStatus.Succeeded) {
      this.handleOnSucceed();
    }
  }

  private onDateCalendarDateSelected(date: Moment | null, dateStr: YYYYMMDD) {
    this.setState({
      dateStr
    });
  }

  private onMinutesChanged(value: number | string | undefined) {
    const minutes = Number(value);
    if(isNaN(minutes)) {
      return;
    }
    this.setState({ minutes });
  }

  private handleAccept() {
    const { dateStr, minutes } = this.state;

    if(dateStr === undefined || minutes === undefined) {
      return;
    }

    this.props.createNewTerm({
      termStartDate: dateStr,
      allocatedSeconds: minutes * 60,
      accountId: this.props.accountId
    });
  }

  private disabledDateHandle(currentDate: Moment): boolean {
    const { minDateConstraint } = this.props;
    if(!minDateConstraint) {
      return false;
    }
    const constraintDate = moment.utc(minDateConstraint);
    return currentDate.isBefore(constraintDate.add(1, 'days'));
  }

  private handleCancel() {
    this.props.onCancel();
  }

  private handleOnSucceed() {
    this.props.onSucceeded?.();
  }

  private renderCalendarInput() {
    const currentValue = this.state.dateStr ? moment(this.state.dateStr) : undefined;
    return (
      <Input.Group className={styles.term}>
        <label>Term start date</label>
        <DatePicker
          disabled={!!this.props.disabled}
          value={currentValue}
          onChange={this.onDateCalendarDateSelected}
          disabledDate={this.disabledDateHandle}
        />
      </Input.Group>
    );
  }

  private renderMinutesInput() {
    return (
      <div className={styles.minutesWrapper}>
        <label >Video minutes</label>
        <InputNumber
          disabled={!!this.props.disabled}
          type='number'
          className={styles.minutes}
          id='minutes-input'
          min={1}
          onChange={this.onMinutesChanged}
          value={this.state.minutes}
        />
      </div>
    );
  }

  private renderAcceptCTA() {
    const { minDateConstraint } = this.props;

    let btnTxt = 'Start new term';
    if(!minDateConstraint) {
      btnTxt = 'Create new term';
    }

    return (
      <div className={cx(styles.cta, styles.accept)}>
        <Button disabled={!!this.props.disabled} onClick={this.handleAccept} type='primary'>{btnTxt}</Button>
      </div>
    );
  }

  private renderCancelCTA() {
    return (
      <div className={cx(styles.cta, styles.cancel)}>
        <Button disabled={!!this.props.disabled} onClick={this.handleCancel} className={styles.btn} type='link'>Cancel</Button>
      </div>
    );
  }

  render(): ReactNode {
    return (
      <div className={styles.main}>
        {this.renderCalendarInput()}
        {this.renderMinutesInput()}
        {this.renderAcceptCTA()}
        {this.renderCancelCTA()}
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  const [currentPeriodData] = selectDataOfLastTwoStartPeriods(state);
  let minDateConstraint: YYYYMMDD | undefined = undefined;

  if(currentPeriodData) {
    const { item: { termStartDate } } = currentPeriodData;
    minDateConstraint = termStartDate.substring(0, termStartDate.indexOf('T'));
  }

  return {
    createNewTermRequestStatus: selectCreateNewTermRequestStatus(state),
    minDateConstraint
  };
};

const mapDispatchToProps: IMapDispacthToProps = {
  createNewTerm
};

const CreateNewTerm = connect(mapStateToProps, mapDispatchToProps)(CreateNewTermBase);

export { CreateNewTerm };
